import {
    Alert,
    AlertType,
    ColorField,
    DateField,
    InputCheckboxField,
    InputTextField,
    LoadingView,
    Modal,
    SelectorField,
    TextareaField,
} from '@cuatroochenta/co-admin-react-library'
import { translate } from '../../../../../I18n/i18n'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Event } from '../../../../model/domain/event'
import { ShareCenterOption } from '../../../../model/domain/share-option'
import { ShareOptionsUtils } from '../../../../../utils/map-utils'
import AdvancedShareWithCentersSelector from '../../../common/components/AdvancedShareWithCentersSelector/AdvancedShareWithCentersSelector'
import AdvancedShareWithCentersToolbar from '../../../common/components/AdvancedShareWithCentersToolbar/AdvancedShareWithCentersToolbar'
import './SaveEventModal.scss'

enum ReminderUnitOfTime {
    days = 1440,
    hours = 60,
    minutes = 1,
}

const reminderTimeOptions = {
    [ReminderUnitOfTime.days]: [1, 2, 3, 4, 5, 6, 8, 10, 15],
    [ReminderUnitOfTime.hours]: [1, 2, 3, 4, 5, 6, 8, 10, 15],
    [ReminderUnitOfTime.minutes]: [5, 10, 15, 20, 30, 45],
}

interface SaveEventModalProps {
    show: boolean
    loading: boolean
    event: Event | null
    shareOptions?: ShareCenterOption[]
    loadingShareOptions: boolean
    onConfirm: (event: Event) => void
    onCancel: () => void
}

interface SaveEventModalState {
    title: string
    startDate: string
    endDate: string
    allDay: string
    reminderOn: string
    reminderUnitOfTime: ReminderUnitOfTime
    reminderTime: number
    color: string
    description: string
    error: string
    showShareSelector: boolean
    shareOptions?: ShareCenterOption[]
    selectedNames: string[]
}

export default function SaveEventModal({
    show,
    loading,
    event,
    shareOptions: initialShareOptions,
    loadingShareOptions,
    onConfirm,
    onCancel,
}: SaveEventModalProps) {
    const { t } = useTranslation()
    const [form, setForm] = useState<SaveEventModalState>({
        title: '',
        startDate: '',
        endDate: '',
        allDay: '',
        reminderOn: '',
        reminderUnitOfTime: ReminderUnitOfTime.minutes,
        reminderTime: 5,
        color: '',
        description: '',
        error: '',
        showShareSelector: false,
        selectedNames: [],
        shareOptions: initialShareOptions,
    })

    useEffect(() => {
        if (event) {
            handleFormChange({
                title: event.title ?? title,
                startDate: event.startDate ?? startDate,
                endDate: event.endDate ?? endDate,
                color: event.color ?? color,
                description: event.description ?? description,
                allDay: event.allDay ? event.allDay.toString() : allDay,
                ...parseReminderTimeFromMinutes(event.reminderTime),
                shareOptions: event.shareOptions,
            })
        } else {
            resetForm()
        }
    }, [event])

    useEffect(() => {
        if (!form.shareOptions || form.shareOptions.length === 0) {
            handleFormChange({ shareOptions: initialShareOptions })
        }
    }, [initialShareOptions, form.shareOptions])

    useEffect(() => {
        if (form.allDay) {
            handleFormChange({ endDate: '' })
        }
    }, [form.allDay])

    useEffect(() => {
        if (form.startDate && form.endDate) {
            const parsedStartDate = new Date(form.startDate)
            const parsedEndDate = new Date(form.endDate)

            if (parsedEndDate < parsedStartDate) {
                handleFormChange({ endDate: form.startDate })
            }
        }
    }, [form.startDate, form.endDate])

    useEffect(() => {
        if (form.endDate) {
            handleFormChange({ allDay: '' })
        }
    }, [form.endDate])

    const handleFormChange = (changes: Partial<SaveEventModalState>) => {
        setForm((form) => ({
            ...form,
            ...changes,
        }))
    }

    const {
        title,
        startDate,
        endDate,
        allDay,
        reminderOn,
        reminderTime,
        reminderUnitOfTime,
        color,
        description,
        error,
        showShareSelector,
        selectedNames,
        shareOptions,
    } = form

    useEffect(() => {
        const selectedNames = ShareOptionsUtils.getSelectedNames(shareOptions)
        handleFormChange({ selectedNames })
    }, [shareOptions])

    const minEndDate = useMemo(() => {
        const parsedStartDate = startDate ? new Date(startDate) : undefined
        parsedStartDate?.setMinutes(parsedStartDate.getMinutes() + 5)
        return parsedStartDate
    }, [startDate])

    const currentReminderTimeOptions = useMemo(() => {
        return reminderTimeOptions[reminderUnitOfTime].map((option) => ({
            id: option,
            description: option.toString(),
        }))
    }, [reminderUnitOfTime])

    const reminderUnitOfTimeOptions = useMemo(() => {
        const options = {
            [ReminderUnitOfTime.days]:
                'calendar.TR_CREATE_MODAL_FORM_REMINDER_DAYS',
            [ReminderUnitOfTime.hours]:
                'calendar.TR_CREATE_MODAL_FORM_REMINDER_HOURS',
            [ReminderUnitOfTime.minutes]:
                'calendar.TR_CREATE_MODAL_FORM_REMINDER_MINUTES',
        }

        return Object.entries(options).map(([unitOfTime, translation]) => ({
            id: +unitOfTime,
            description: translate(t, translation),
        }))
    }, [t])

    const parseReminderTimeFromMinutes = (totalMinutes?: number) => {
        for (const [unit, validValues] of Object.entries(reminderTimeOptions)) {
            const unitValue = parseInt(unit, 10)
            for (const value of validValues) {
                if (value * unitValue === totalMinutes) {
                    return {
                        reminderOn: 'true',
                        reminderUnitOfTime: unitValue as ReminderUnitOfTime,
                        reminderTime: value,
                    }
                }
            }
        }

        return {
            reminderOn: '',
            reminderUnitOfTime: ReminderUnitOfTime.minutes,
            reminderTime: 5,
        }
    }

    const handleOpenCenterSelector = () => {
        handleFormChange({ showShareSelector: true })
    }

    const handleCancelCenterSelection = () => {
        handleFormChange({
            selectedNames: [],
            showShareSelector: false,
            shareOptions: shareOptions?.map((center) =>
                ShareOptionsUtils.copyCenterRecursive(center, {
                    checked: false,
                })
            ),
        })
    }

    const handleAcceptCenterSelection = () => {
        handleFormChange({ showShareSelector: false })
    }

    const handleCenterSelection = (selection?: ShareCenterOption[]) => {
        handleFormChange({ shareOptions: selection })
    }

    const resetForm = () => {
        setForm({
            title: '',
            startDate: '',
            endDate: '',
            allDay: '',
            reminderOn: '',
            reminderUnitOfTime: ReminderUnitOfTime.minutes,
            reminderTime: 5,
            color: '',
            description: '',
            error: '',
            showShareSelector: false,
            selectedNames: [],
        })
    }

    const handleCancel = () => {
        resetForm()
        onCancel()
    }

    const handleSubmit = () => {
        try {
            if (
                !title ||
                !startDate ||
                (startDate && !endDate && !allDay) ||
                !color
            ) {
                return handleFormChange({
                    error: 'calendar.TR_CREATE_MODAL_ERROR_EMPTY_FIELDS',
                })
            }

            if (endDate) {
                const parsedEndDate = new Date(endDate)
                if (parsedEndDate < minEndDate!) {
                    return handleFormChange({
                        error: 'calendar.TR_CREATE_MODAL_ERROR_END_DATE_BEFORE',
                    })
                }

                if (allDay) {
                    return handleFormChange({
                        error: 'calendar.TR_CREATE_MODAL_ERROR_END_DATE_ALL_DAY',
                    })
                }
            }

            const newEvent: Event = {
                id: event?.id,
                title,
                description,
                color,
                startDate,
                endDate,
                allDay: !!allDay,
                reminderTime: reminderOn
                    ? reminderTime * reminderUnitOfTime
                    : undefined,
                shareOptions,
                forAllCenters: false,
            }

            resetForm()
            onConfirm(newEvent)
        } catch (err) {
            handleFormChange({
                error: 'calendar.TR_CREATE_MODAL_ERROR_GENERIC',
            })
        }
    }

    const renderCenterSelector = () => (
        <AdvancedShareWithCentersSelector
            loading={loadingShareOptions}
            shareOptions={shareOptions}
            onCancel={handleCancelCenterSelection}
            onAccept={handleAcceptCenterSelection}
            onSelection={handleCenterSelection}
        />
    )

    const renderForm = () => (
        <div className="form-fields">
            <Alert
                show={!!error}
                type={AlertType.DANGER}
                message={translate(t, error)}
            />
            <InputTextField
                name="title"
                type="text"
                size={255}
                value={title}
                onChange={(value) => handleFormChange({ title: value })}
                placeholder={translate(
                    t,
                    'calendar.TR_CREATE_MODAL_FORM_TITLE_PLACEHOLDER'
                )}
            />
            <div className="form-row">
                <label htmlFor="startDate">
                    {translate(
                        t,
                        'calendar.TR_CREATE_MODAL_FORM_START_DATE_LABEL'
                    )}
                </label>
                <DateField
                    name="startDate"
                    type="DATETIME"
                    value={startDate}
                    minDate={new Date()}
                    maxDate={endDate ? new Date(endDate) : undefined}
                    minTime={new Date()}
                    maxTime={endDate ? new Date(endDate) : undefined}
                    onChange={(value) => handleFormChange({ startDate: value })}
                    placeholder={translate(
                        t,
                        'calendar.TR_CREATE_MODAL_FORM_START_DATE_LABEL'
                    )}
                />
            </div>
            <div className="form-row">
                <label htmlFor="endDate">
                    {translate(
                        t,
                        'calendar.TR_CREATE_MODAL_FORM_END_DATE_LABEL'
                    )}
                </label>
                <DateField
                    name="endDate"
                    type="DATETIME"
                    value={endDate}
                    onChange={(value) => handleFormChange({ endDate: value })}
                    placeholder={translate(
                        t,
                        'calendar.TR_CREATE_MODAL_FORM_END_DATE_LABEL'
                    )}
                    disabled={!startDate || !!allDay}
                    minDate={minEndDate}
                    minTime={new Date(startDate)}
                />
            </div>
            <InputCheckboxField
                name="allDay"
                label={translate(
                    t,
                    'calendar.TR_CREATE_MODAL_FORM_ALL_DAY_LABEL'
                )}
                value={allDay}
                onChange={(value) => handleFormChange({ allDay: value })}
            />
            <div className="reminder-container">
                <InputCheckboxField
                    name="reminderOn"
                    label={translate(
                        t,
                        'calendar.TR_CREATE_MODAL_FORM_REMINDER_TOGGLE_LABEL'
                    )}
                    value={reminderOn}
                    onChange={(value) =>
                        handleFormChange({ reminderOn: value })
                    }
                />
                {reminderOn ? (
                    <div className="form-row">
                        <SelectorField
                            value={reminderTime?.toString()}
                            onChange={(value) =>
                                handleFormChange({ reminderTime: value })
                            }
                            includeAllOption={false}
                            values={currentReminderTimeOptions}
                        />
                        <SelectorField
                            value={reminderUnitOfTime.toString()}
                            onChange={(value) =>
                                handleFormChange({ reminderUnitOfTime: value })
                            }
                            includeAllOption={false}
                            values={reminderUnitOfTimeOptions}
                        />
                        <span>
                            {translate(
                                t,
                                'calendar.TR_CREATE_MODAL_FORM_REMINDER_TIME_LABEL'
                            )}
                        </span>
                    </div>
                ) : null}
            </div>
            <ColorField
                name="color"
                placeholder={translate(
                    t,
                    'calendar.TR_CREATE_MODAL_FORM_COLOR_LABEL'
                )}
                value={color}
                onChange={(value) => handleFormChange({ color: value })}
            />
            <TextareaField
                name="description"
                placeholder={translate(
                    t,
                    'calendar.TR_CREATE_MODAL_FORM_DESCRIPTION_PLACEHOLDER'
                )}
                value={description}
                onChange={(value) => handleFormChange({ description: value })}
            />
            <AdvancedShareWithCentersToolbar
                selectedOptions={selectedNames.join(', ')}
                onClick={handleOpenCenterSelector}
            />
        </div>
    )

    return (
        <Modal
            show={show}
            title={
                loading
                    ? ''
                    : translate(
                          t,
                          event
                              ? 'calendar.TR_EDIT_MODAL_TITLE'
                              : 'calendar.TR_CREATE_MODAL_TITLE'
                      )
            }
            acceptText={translate(t, 'calendar.TR_CREATE_MODAL_PUBLISH')}
            acceptHandler={handleSubmit}
            disabledAcceptHandler={loading}
            showHeader={!showShareSelector}
            showFooter={!showShareSelector}
            onClose={handleCancel}
            fitContentWidth={showShareSelector}
            children={
                loading ? (
                    <LoadingView loading />
                ) : showShareSelector ? (
                    renderCenterSelector()
                ) : (
                    renderForm()
                )
            }
        />
    )
}
